import React, { useMemo } from 'react'
import { FieldErrors, UseFormClearErrors, UseFormRegister, UseFormSetValue } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Avatar, Box, Checkbox, Combobox, Input, Radio, Text } from '@node-space/ui-components'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { ContactFiatInstrumentFormData } from 'pages/Contacts/schemas/contactFiatInstrumentSchema'
import { FormFieldLabels } from 'pages/Contacts/types/fiatInstruments'
import {
  BeneficiaryDetailsCurrency,
  BeneficiaryEntityType,
  BeneficiaryTransferDestination,
} from 'types/beneficiaries'
import { AddressDetails } from './AddressDetails'
import { InternationalBankDetails } from './InternationalBankDetails'
import { LocalBankDetails } from './LocalBankDetails'

type BankAccountDetailsProps = {
  formValues: ContactFiatInstrumentFormData
  errors: FieldErrors<ContactFiatInstrumentFormData>
  isBusinessAccount: boolean
  isInternationalBankAccount: boolean
  formFieldLabels: FormFieldLabels
  clearErrors: UseFormClearErrors<ContactFiatInstrumentFormData>
  setValue: UseFormSetValue<ContactFiatInstrumentFormData>
  register: UseFormRegister<ContactFiatInstrumentFormData>
}
export const BankAccountDetails = ({
  formValues,
  errors,
  isBusinessAccount,
  isInternationalBankAccount,
  formFieldLabels,
  clearErrors,
  setValue,
  register,
}: BankAccountDetailsProps) => {
  const { t } = useTranslation()
  const { accountCapabilities } = useAccountsContext()
  const {
    isCubixBeneficiaryCapabilityEnabled,
    isUsdAchFedwireBeneficiaryCapabilityEnabled,
    isUsdCRNowBeneficiaryCapabilityEnabled,
    isUsdRtpBeneficiaryCapabilityEnabled,
    isUsdFedNowBeneficiaryCapabilityEnabled,
    isAedBeneficiaryEnabled,
  } = accountCapabilities || {}

  const isSwiftBankAccount =
    formValues?.transferDestination === BeneficiaryTransferDestination.INTERNATIONAL
  const isLocalBankAccount =
    formValues?.transferDestination === BeneficiaryTransferDestination.LOCAL
  const isCurrencySelected = !!formValues?.currency

  const entityOptions = [
    {
      value: BeneficiaryEntityType.BUSINESS,
      label: t('manageBeneficiaries.business'),
    },
    {
      value: BeneficiaryEntityType.INDIVIDUAL,
      label: t('manageBeneficiaries.individual'),
    },
  ]

  const currencies = useMemo(() => {
    const beneficiaryAccountCurrencies: string[] = [
      BeneficiaryDetailsCurrency.EUR,
      BeneficiaryDetailsCurrency.GBP,
    ]

    const isUSDAvailable =
      ((formValues?.entityType === BeneficiaryEntityType.BUSINESS &&
        isCubixBeneficiaryCapabilityEnabled) ||
        isUsdAchFedwireBeneficiaryCapabilityEnabled ||
        isUsdCRNowBeneficiaryCapabilityEnabled ||
        isUsdRtpBeneficiaryCapabilityEnabled ||
        isUsdFedNowBeneficiaryCapabilityEnabled ||
        isSwiftBankAccount) &&
      !beneficiaryAccountCurrencies.includes(BeneficiaryDetailsCurrency.USD)

    if (isUSDAvailable) {
      beneficiaryAccountCurrencies.push(BeneficiaryDetailsCurrency.USD)
    }

    if (
      isSwiftBankAccount &&
      isAedBeneficiaryEnabled &&
      !beneficiaryAccountCurrencies.includes(BeneficiaryDetailsCurrency.AED)
    ) {
      beneficiaryAccountCurrencies.push(BeneficiaryDetailsCurrency.AED)
    }

    return beneficiaryAccountCurrencies.map(currency => ({
      LeftElement: <Avatar name={currency.toLowerCase()} size="small" />,
      leftText: currency,
      value: currency,
    }))
  }, [formValues?.entityType, isCubixBeneficiaryCapabilityEnabled, isSwiftBankAccount])

  const onEntityTypeChange = (value: string) => {
    setValue('entityType', value)
  }

  const onCurrencyChange = (value: string) => {
    setValue('currency', value)
  }

  const handleInputChange =
    (field: keyof ContactFiatInstrumentFormData) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setValue(field, event.target.value, {
        shouldValidate: true,
        shouldDirty: true,
      })
    }

  return (
    <Box width="full">
      <Box
        flexCol
        width="full"
        gapY={16}
        paddingT={24}
        paddingB={isInternationalBankAccount ? null : 12}
      >
        <Radio
          {...register('entityType')}
          name="entityType"
          horizontal
          onChange={value => onEntityTypeChange(value)}
          key="entityType"
          testId="entity-type"
          options={entityOptions}
          value={formValues?.entityType}
          error={errors?.entityType?.message}
        />
        <Combobox
          {...register('currency')}
          id="currency"
          testId="currency"
          label={t('formFields.bankAccountCurrency')}
          items={currencies}
          errorText={errors?.currency?.message}
          defaultValue={formValues?.currency}
          onChange={value => onCurrencyChange(value)}
        />
        <Input
          {...register('description')}
          testId="description"
          label={t('formFields.beneficiaryDescription')}
          placeholder={t('formFields.placeholder.beneficiaryDescription')}
          errorText={errors?.description?.message}
          error={!!errors?.description?.message}
          onChange={handleInputChange('description')}
        />
        <Input
          {...register('paymentReference')}
          testId="paymentReference"
          label={t('formFields.paymentReference')}
          optionalText={t('optional')}
          placeholder={t('formFields.placeholder.paymentReference')}
          errorText={errors?.paymentReference?.message}
          error={!!errors?.paymentReference?.message}
          value={formValues?.paymentReference}
          onChange={handleInputChange('paymentReference')}
        />
        {isLocalBankAccount && isCurrencySelected && (
          <LocalBankDetails
            formFieldLabels={formFieldLabels}
            formValues={formValues}
            isBusinessAccount={isBusinessAccount}
            errors={errors}
            setValue={setValue}
            clearErrors={clearErrors}
            register={register}
          />
        )}
        {isCurrencySelected && (
          <>
            {isLocalBankAccount && (
              <Text variant="heading-2">{t('formFields.bankAddressDetails')}</Text>
            )}
            <AddressDetails
              formValues={formValues}
              errors={errors}
              type="beneficiary"
              clearErrors={clearErrors}
              isInternationalBankAccount={isInternationalBankAccount}
              register={register}
              setValue={setValue}
            />
          </>
        )}
        {isSwiftBankAccount && isCurrencySelected && (
          <InternationalBankDetails
            formFieldLabels={formFieldLabels}
            formValues={formValues}
            setValue={setValue}
            clearErrors={clearErrors}
            register={register}
            errors={errors}
          />
        )}
        {isBusinessAccount && (
          <Checkbox
            {...register('isSelfOwned')}
            id="isSelfOwned"
            testId="is-self-owned"
            label={t('formFields.selfOwnedBankAccount')}
            checked={formValues?.isSelfOwned}
            onCheckedChange={(checked: boolean) => setValue('isSelfOwned', checked)}
          />
        )}
      </Box>
    </Box>
  )
}
